import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import './QRScanner.css';

interface QRScannerProps {
  onClose: () => void;
  onScan: (data: string) => void;
}

const QRScanner: React.FC<QRScannerProps> = ({ onClose, onScan }) => {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      setScanning(true);
      setError(null);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      setError('Camera access denied. Please allow camera access to scan QR codes.');
      setScanning(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const handleManualInput = () => {
    const qrData = prompt('Enter QR code data manually:');
    if (qrData) {
      onScan(qrData);
    }
  };

  const simulateQRScan = () => {
    // Simulate scanning a QR code for demo purposes
    const demoQRData = JSON.stringify({
      type: 'beneficiary',
      data: {
        name: 'Demo Merchant',
        email: 'demo@merchant.com',
        accountNumber: '1234567890',
        bankName: 'Demo Bank',
        trustLevel: 'public'
      },
      timestamp: new Date().toISOString()
    });
    
    onScan(demoQRData);
  };

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="modal-content"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Scan QR Code</h2>
          <motion.button
            className="close-btn"
            onClick={onClose}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            ✕
          </motion.button>
        </div>

        <div className="scanner-content">
          {error ? (
            <div className="scanner-error">
              <div className="error-icon">📷</div>
              <p>{error}</p>
              <motion.button
                className="retry-btn"
                onClick={startCamera}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Try Again
              </motion.button>
            </div>
          ) : (
            <div className="scanner-viewport">
              <video
                ref={videoRef}
                className="scanner-video"
                autoPlay
                playsInline
                muted
              />
              <canvas
                ref={canvasRef}
                className="scanner-canvas"
                style={{ display: 'none' }}
              />
              
              <div className="scanner-overlay">
                <div className="scanner-frame">
                  <div className="corner top-left"></div>
                  <div className="corner top-right"></div>
                  <div className="corner bottom-left"></div>
                  <div className="corner bottom-right"></div>
                </div>
                
                <div className="scanner-instructions">
                  <p>Position the QR code within the frame</p>
                  {scanning && (
                    <motion.div
                      className="scanning-indicator"
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      Scanning...
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="scanner-actions">
          <motion.button
            className="action-btn secondary"
            onClick={handleManualInput}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Manual Input
          </motion.button>
          
          <motion.button
            className="action-btn primary"
            onClick={simulateQRScan}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Demo Scan
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default QRScanner;
